import { compact } from 'lodash';
import { Control, FieldValues } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FieldNameFromFormShape } from '@/types/react-hook-form';
import { EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { EntityType } from '../../types/EntityType';
import { getEntityTypeFromSubtype } from '../../utils/getEntityTypeFromSubtype';
import { BeneficiaryDropdown_HouseholdFragment } from './graphql/BeneficiaryDropdown.generated';

export function getOptionsFromData(
  data: BeneficiaryDropdown_HouseholdFragment | null,
  selectedIds: string[],
  opts?: {
    filterEntityTypes?: EntityType[];
  }
): TypeaheadSelectInputOption<string>[] {
  const possibleClientOptions =
    data?.clientProfiles?.map((cp) => {
      return {
        display: cp.displayName,
        value: cp.id,
        groupName: 'Individuals',
      };
    }) ?? [];

  const possibleEntityOptions = compact(
    getNodes(data?.entities).map((e) => {
      if (!e.subtype.__typename) {
        return null;
      }

      // Filter out pre-created entities
      if (e.stage === EntityStage.PreCreated) {
        return null;
      }

      const entityType: EntityType = getEntityTypeFromSubtype(
        e.subtype.__typename
      );

      if (
        opts?.filterEntityTypes &&
        opts.filterEntityTypes.includes(entityType)
      ) {
        return null;
      }

      return {
        display: e.subtype.displayName,
        value: e.id,
        groupName: 'Entities',
      };
    })
  );

  const possibleOrganizationOptions =
    data?.clientOrganizations?.map((o) => {
      return {
        display: o.name,
        value: o.id,
        groupName: 'Organizations',
      };
    }) ?? [];

  const possibleOptions = [
    ...possibleClientOptions,
    ...possibleOrganizationOptions,
    ...possibleEntityOptions,
  ];

  return getBeneficiaryOptionsWithSelectedDisabled({
    possibleOptions,
    selectedIds,
  });
}

function getBeneficiaryOptionsWithSelectedDisabled({
  possibleOptions,
  selectedIds,
}: {
  possibleOptions: ReturnType<typeof getOptionsFromData>;
  selectedIds: string[];
}) {
  return possibleOptions.map((option) => {
    return {
      ...option,
      disabled: selectedIds.includes(option.value),
    };
  });
}

type BeneficiaryDropdownSingleFormShape = FieldValues & {
  beneficiaryId?: string;
};

export interface BeneficiaryDropdownSingleProps<
  FormShape extends BeneficiaryDropdownSingleFormShape,
> extends Partial<FormAwareTypeaheadSelectInputProps<FormShape>> {
  control: Control<FormShape>;
  namespace: FieldNameFromFormShape<FormShape>;
  options: TypeaheadSelectInputOption<string>[];
}

export function BeneficiaryDropdownSingle<
  FormShape extends BeneficiaryDropdownSingleFormShape,
>({
  control,
  namespace,
  options,
  ...rest
}: BeneficiaryDropdownSingleProps<FormShape>) {
  return (
    <FormAwareTypeaheadSelectInput<FormShape>
      control={control}
      fieldName={`${namespace}.beneficiary.beneficiaryId`}
      label="Beneficiary"
      options={options}
      placeholder="Search beneficiaries"
      groupBy={(option) => option.groupName ?? ''}
      {...rest}
    />
  );
}
